import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const solutions = [
  {
    name: 'Card Access Systems',
    description: 'Traditional proximity cards and smart cards for reliable, cost-effective access control.',
    icon: 'ri-bank-card-line',
    image: 'https://public.readdy.ai/ai/img_res/41b652fd2b20f2a503a2ae6127bc9954.jpg',
    features: ['Easy to use', 'Cost effective', 'Scalable solution', 'Quick deployment']
  },
  {
    name: 'Key Fob Systems',
    description: 'Compact, durable key fobs perfect for high-traffic environments and multi-tenant buildings.',
    icon: 'ri-key-2-line',
    image: 'https://readdy.ai/api/search-image?query=modern%20key%20fob%20access%20control%20system%20small%20electronic%20key%20fob%20device%20contemporary%20office%20building%20entrance%20professional%20security%20equipment%20clean%20design&width=800&height=600&seq=keyfob&orientation=landscape',
    features: ['Portable & durable', 'Multi-facility access', 'Easy replacement', 'User-friendly']
  },
  {
    name: 'Biometric Access',
    description: 'Fingerprint and facial recognition for maximum security in sensitive areas.',
    icon: 'ri-fingerprint-line',
    image: 'https://readdy.ai/api/search-image?query=biometric%20fingerprint%20scanner%20access%20control%20system%20modern%20fingerprint%20reader%20device%20high-tech%20security%20equipment%20sleek%20professional%20design%20contemporary%20office%20setting&width=800&height=600&seq=biometric&orientation=landscape',
    features: ['Highest security', 'No lost credentials', 'Unique identification', 'Audit compliance']
  },
  {
    name: 'Mobile Credentials',
    description: 'Smartphone-based access using Bluetooth or NFC for modern, contactless entry.',
    icon: 'ri-smartphone-line',
    image: 'https://readdy.ai/api/search-image?query=mobile%20phone%20access%20control%20system%20person%20using%20smartphone%20to%20unlock%20door%20modern%20contactless%20entry%20technology%20contemporary%20office%20environment%20professional%20security&width=800&height=600&seq=mobileaccess&orientation=landscape',
    features: ['Contactless entry', 'Remote management', 'No physical cards', 'Modern solution']
  }
];

const features = [
  {
    icon: 'ri-time-line',
    title: 'Time-Based Access',
    description: 'Set specific hours when employees can enter. Automatically restrict access outside business hours or on holidays.'
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Zone Control',
    description: 'Grant different access levels to different areas. Restrict sensitive zones to authorized personnel only.'
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Detailed Logs',
    description: 'Every entry and exit is recorded with timestamp and user ID. Perfect for compliance and investigations.'
  },
  {
    icon: 'ri-notification-3-line',
    title: 'Real-Time Alerts',
    description: 'Get instant notifications for unauthorized access attempts, forced entry, or door-held-open events.'
  },
  {
    icon: 'ri-cloud-line',
    title: 'Cloud Management',
    description: 'Manage your entire system from anywhere. Add or remove users, change permissions, and view reports remotely.'
  },
  {
    icon: 'ri-lock-unlock-line',
    title: 'Remote Unlock',
    description: 'Grant temporary access to visitors or contractors without being on-site. Unlock doors remotely when needed.'
  }
];

const useCases = [
  {
    icon: 'ri-building-2-line',
    title: 'Office Buildings',
    description: 'Control access to your office, restrict sensitive areas, and track employee attendance automatically.',
    benefits: [
      'Employee time tracking',
      'Visitor management',
      'After-hours security',
      'Conference room access'
    ]
  },
  {
    icon: 'ri-hospital-line',
    title: 'Healthcare Facilities',
    description: 'Protect patient privacy and secure medication storage with HIPAA-compliant access control.',
    benefits: [
      'HIPAA compliance',
      'Pharmacy security',
      'Staff credentialing',
      'Emergency lockdown'
    ]
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail & Warehouses',
    description: 'Secure inventory areas, loading docks, and back offices while maintaining smooth operations.',
    benefits: [
      'Inventory protection',
      'Loading dock control',
      'Staff-only areas',
      'Theft prevention'
    ]
  },
  {
    icon: 'ri-government-line',
    title: 'Government Buildings',
    description: 'Meet strict security requirements with multi-factor authentication and detailed audit trails.',
    benefits: [
      'High security clearance',
      'Audit trail compliance',
      'Visitor screening',
      'Emergency protocols'
    ]
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Educational Institutions',
    description: 'Protect students and staff with controlled campus access and emergency lockdown capabilities.',
    benefits: [
      'Campus-wide security',
      'Dorm access control',
      'Lab restrictions',
      'Emergency lockdown'
    ]
  },
  {
    icon: 'ri-home-4-line',
    title: 'Multi-Family Residential',
    description: 'Provide secure building entry, amenity access, and package room control for residents.',
    benefits: [
      'Building entry security',
      'Amenity room access',
      'Package room control',
      'Tenant management'
    ]
  }
];

const faqs = [
  {
    question: 'How much does an access control system cost?',
    answer: 'Costs vary based on the number of doors, type of credentials (cards, fobs, biometrics), and features needed. A basic 2-door system starts around $2,500-$3,500 installed, while larger commercial systems can range from $5,000-$15,000+. We provide free site assessments and transparent quotes with no hidden fees.'
  },
  {
    question: 'Can I integrate access control with my existing security cameras?',
    answer: 'Yes! We specialize in integrated security solutions. When access control is linked with your camera system, you can automatically record video whenever a door is accessed, making investigations much easier. We can integrate with most modern camera systems.'
  },
  {
    question: 'What happens if the internet goes down?',
    answer: 'Most modern access control systems have local controllers that continue to function during internet outages. Your employees can still enter using their credentials, and all access events are stored locally and synced to the cloud once connectivity is restored.'
  },
  {
    question: 'How do I add or remove users?',
    answer: 'With cloud-based systems, you can add or remove users instantly from any device with internet access. Simply log into the management portal, add the new user, assign their access permissions, and issue them a credential. Removing users is just as easy—deactivate their credential with one click.'
  },
  {
    question: 'Can employees use their smartphones instead of cards?',
    answer: 'Yes! Mobile credentials are becoming increasingly popular. Employees can use their smartphones to unlock doors via Bluetooth or NFC. This eliminates the need for physical cards and makes it easy to grant temporary access to visitors or contractors.'
  },
  {
    question: 'Is access control suitable for small businesses?',
    answer: 'Absolutely! Access control isn\'t just for large corporations. Even a single-door system provides significant benefits: no more rekeying locks when employees leave, detailed logs of who entered and when, and the ability to grant temporary access to contractors or cleaners.'
  },
  {
    question: 'How long does installation take?',
    answer: 'Installation time depends on the number of doors and complexity of the system. A typical 2-4 door system can be installed in one day. Larger commercial installations may take 2-5 days. We work efficiently to minimize disruption to your business operations.'
  },
  {
    question: 'What kind of maintenance is required?',
    answer: 'Access control systems require minimal maintenance. We recommend annual inspections to test all readers, check door hardware, and update software. Most issues can be resolved remotely. We offer maintenance plans that include priority support and regular system health checks.'
  }
];

export default function AccessControlPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedSolution, setSelectedSolution] = useState('');
  const [solutionDropdownOpen, setSolutionDropdownOpen] = useState(false);

  const solutionOptions = [
    'Card Access Systems',
    'Key Fob Systems',
    'Biometric Access',
    'Mobile Credentials',
    'Multi-Technology Solution',
    'Not Sure — Need Advice',
  ];

  const scrollToContact = () => {
    const el = document.getElementById('access-consultation-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('solution', selectedSolution);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d6hp5p857726vudt81ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedSolution('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const accessStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Access Control', item: 'https://alliancesecuritysystems.com/access-control' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://alliancesecuritysystems.com/access-control#service',
      name: 'Access Control System Installation',
      description: 'Professional card access, key fob, biometric, and mobile credential systems for businesses. Time-based access, zone control, detailed logs, and cloud management across the GTA.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      areaServed: { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Access Control Solutions',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Card Access Systems', description: 'Proximity and smart card readers for reliable, scalable commercial access control.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Key Fob Systems', description: 'Compact, durable key fobs for high-traffic and multi-tenant buildings.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Biometric Access Control', description: 'Fingerprint and facial recognition for maximum security in sensitive areas.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Mobile Credential Access', description: 'Smartphone-based Bluetooth and NFC entry for modern, contactless access.' },
          },
        ],
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Access Control Systems | Card & Biometric Entry Solutions | Alliance Security Systems"
        description="Professional access control systems for businesses. Card readers, key fobs, biometric & mobile credentials. Control who enters your facility with time-based access, zone control & detailed logs. Free site assessment available."
        keywords="access control systems, card access, biometric access, key fob systems, mobile credentials, commercial security, business access control, access control Toronto"
        canonical="/access-control"
        structuredData={accessStructuredData}
        faqItems={faqs}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Alarm Systems
            </Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Cameras
            </Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Clients
            </Link>
            <a 
              href="tel:18884589181"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              1-888-458-9181
            </a>
            <button 
              onClick={scrollToContact}
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
            <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarm Systems</Link>
            <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Cameras</Link>
            <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"><i className="ri-phone-line"></i>1-888-458-9181</a>
            <button onClick={scrollToContact} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Get Free Quote</button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative py-14 sm:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=bright%20modern%20corporate%20office%20building%20lobby%20with%20large%20glass%20windows%20natural%20daylight%20streaming%20in%20clean%20white%20marble%20floors%20contemporary%20access%20control%20entrance%20professional%20business%20environment%20light%20airy%20atmosphere&width=1920&height=800&seq=accessherolight&orientation=landscape" 
              alt="Access Control Hero"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/85 via-[#0A1628]/60 to-transparent"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-2 text-xs sm:text-sm text-white/70 mb-6 sm:mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span>Services</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Access Control Systems</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block bg-[#D4A853] text-white px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-4 sm:mb-6 whitespace-nowrap">
                SECURE ENTRY MANAGEMENT
              </span>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 sm:mb-6 leading-tight">
                Access Control Systems
              </h1>
              <p className="text-xl text-white/80 mb-8 sm:mb-8 leading-relaxed">
                Modern card access, key fob, and biometric solutions for businesses. Control who enters your facility, when they can enter, and maintain detailed access logs for complete security oversight.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                <button 
                  onClick={scrollToContact}
                  className="bg-[#D4A853] text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Assessment
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </button>
                <a 
                  href="tel:18884589181"
                  className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer text-center"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#0A1628] py-6 sm:py-8" aria-label="Statistics">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {[
                { stat: '5,000+', label: 'Doors Secured' },
                { stat: '500+', label: 'Businesses Protected' },
                { stat: '25+', label: 'Years Experience' },
                { stat: '24/7', label: 'Support Available' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-[#D4A853] mb-1">{item.stat}</p>
                  <p className="text-sm sm:text-sm text-white/70">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What is Access Control */}
        <section className="py-12 sm:py-24 bg-white" aria-labelledby="what-is-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                  UNDERSTANDING ACCESS CONTROL
                </span>
                <h2 id="what-is-heading" className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-6 sm:mb-6">
                  What is Access Control?
                </h2>
                <p className="text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Access control systems replace traditional locks and keys with electronic credentials—cards, fobs, biometrics, or mobile phones. This gives you complete control over who can enter your facility, which areas they can access, and when they're allowed entry.
                </p>
                <p className="text-lg text-gray-600 mb-8 sm:mb-8 leading-relaxed">
                  Unlike traditional keys that can be copied or lost without your knowledge, electronic credentials can be instantly deactivated and every access attempt is logged, giving you a complete audit trail of all activity.
                </p>
                
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Enhanced Security' },
                    { icon: 'ri-file-list-line', text: 'Complete Audit Trails' },
                    { icon: 'ri-settings-3-line', text: 'Easy Management' },
                    { icon: 'ri-money-dollar-circle-line', text: 'Cost Effective' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2 sm:gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#D4A853]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg sm:text-xl text-[#D4A853]`}></i>
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-[#0A1628]">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="w-full h-[300px] sm:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20installing%20access%20control%20card%20reader%20on%20office%20door%20wall%20technician%20working%20on%20security%20system%20modern%20corporate%20environment%20clean%20professional%20installation&width=800&height=600&seq=accessinstall&orientation=landscape" 
                    alt="Access Control Installation"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="absolute -bottom-6 -left-4 sm:-bottom-8 sm:-left-8 bg-[#D4A853] rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
                  <p className="text-3xl sm:text-4xl font-bold text-white mb-1">25+</p>
                  <p className="text-sm sm:text-sm text-white/80">Years Installing<br/>Access Systems</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Control Solutions */}
        <section className="py-12 sm:py-24 bg-gray-50" aria-labelledby="solutions-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                OUR SOLUTIONS
              </span>
              <h2 id="solutions-heading" className="text-2xl sm:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Access Control Technologies
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                From traditional card readers to cutting-edge biometrics, we offer solutions for every security need
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {solutions.map((solution, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative w-full h-48 sm:h-64">
                    <img 
                      src={solution.image}
                      alt={solution.name}
                      className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute top-3 left-3 sm:top-4 sm:left-4 w-11 h-11 sm:w-14 sm:h-14 bg-[#D4A853] rounded-full flex items-center justify-center">
                      <i className={`${solution.icon} text-2xl sm:text-3xl text-white`}></i>
                    </div>
                  </div>
                  <div className="p-5 sm:p-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-2 sm:mb-3">{solution.name}</h3>
                    <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">{solution.description}</p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <i className="ri-check-line text-[#D4A853] flex-shrink-0"></i>
                          <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-12 sm:py-24 bg-[#0A1628]" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853] text-white px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                POWERFUL FEATURES
              </span>
              <h2 id="features-heading" className="text-2xl sm:text-5xl font-bold text-white mb-3 sm:mb-4">
                Complete Control at Your Fingertips
              </h2>
              <p className="text-sm sm:text-lg text-white/70 max-w-2xl mx-auto">
                Our access control systems come packed with features to give you total security oversight
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-white/10 hover:bg-white/10 transition-all"
                >
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-[#D4A853] rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${feature.icon} text-2xl sm:text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-white/70 leading-relaxed text-xs sm:text-base">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-12 sm:py-24 bg-white" aria-labelledby="industries-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                INDUSTRY SOLUTIONS
              </span>
              <h2 id="industries-heading" className="text-2xl sm:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                Access Control for Every Industry
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Tailored solutions designed for the unique security challenges of your business
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {useCases.map((useCase, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853]/10 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${useCase.icon} text-2xl sm:text-3xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">{useCase.title}</h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">{useCase.description}</p>
                  <ul className="space-y-1.5 sm:space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                        <i className="ri-check-double-line text-[#D4A853] flex-shrink-0"></i>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 sm:py-24 bg-gray-50" aria-labelledby="process-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10 sm:mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                SIMPLE PROCESS
              </span>
              <h2 id="process-heading" className="text-2xl sm:text-5xl font-bold text-[#0A1628] mb-3 sm:mb-4">
                How It Works
              </h2>
              <p className="text-sm sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Getting started with access control is easy with our proven process
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {[
                {
                  step: '01',
                  title: 'Free Assessment',
                  description: 'We evaluate your facility, identify entry points, and understand your security requirements.',
                  icon: 'ri-search-line'
                },
                {
                  step: '02',
                  title: 'Custom Design',
                  description: 'Our experts design a tailored access control system that fits your needs and budget.',
                  icon: 'ri-draft-line'
                },
                {
                  step: '03',
                  title: 'Professional Install',
                  description: 'Our certified technicians install your system with minimal disruption to your operations.',
                  icon: 'ri-tools-line'
                },
                {
                  step: '04',
                  title: 'Training & Support',
                  description: 'We train your team and provide ongoing support to ensure smooth operation.',
                  icon: 'ri-customer-service-2-line'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg h-full">
                    <span className="text-4xl sm:text-6xl font-extrabold text-[#D4A853]/20">{item.step}</span>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4A853] rounded-full flex items-center justify-center mb-3 sm:mb-4 -mt-4 sm:-mt-6 relative z-10">
                      <i className={`${item.icon} text-xl sm:text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-base sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-gray-600 text-xs sm:text-base">{item.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-[#D4A853]">
                      <i className="ri-arrow-right-line text-2xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-24 bg-white" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-4 sm:px-6 py-2 rounded-full text-sm font-semibold mb-3 sm:mb-4 whitespace-nowrap">
                COMMON QUESTIONS
              </span>
              <h2 id="faq-heading" className="text-2xl sm:text-4xl font-bold text-[#0A1628] mb-3 sm:mb-4">Frequently Asked Questions</h2>
              <p className="text-sm sm:text-lg text-gray-600">Everything you need to know about access control systems</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden">
                  <button 
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4 text-sm sm:text-base">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-xl sm:text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Free Consultation Form Section */}
        <section id="access-consultation-form" className="py-12 sm:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  FREE CONSULTATION
                </span>
                <h2 className="text-3xl sm:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
                  Get a Free Access Control Assessment
                </h2>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                  Let our experts evaluate your facility and design a custom access control solution. We'll assess your entry points, recommend the right technology, and provide a transparent quote — all at no cost.
                </p>

                <div className="space-y-6 mb-10">
                  {[
                    { icon: 'ri-timer-line', title: 'Quick Response', desc: 'We\'ll get back to you within 2 business hours' },
                    { icon: 'ri-building-2-line', title: 'Site Survey', desc: 'Free on-site evaluation of all entry points' },
                    { icon: 'ri-file-text-line', title: 'Custom Proposal', desc: 'Tailored system design with transparent pricing' },
                    { icon: 'ri-shield-check-line', title: 'No Obligation', desc: 'Zero pressure — the assessment is completely free' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-[#0A1628] mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-5 bg-[#0A1628] rounded-2xl">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">Prefer to talk now?</p>
                    <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-200 shadow-lg">
                {formStatus === 'success' ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="ri-check-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Request Received!</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                      Thank you for your interest. Our access control specialists will contact you within 2 business hours to schedule your free site assessment.
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-2">Get Your Free Quote</h3>
                    <p className="text-sm text-gray-500 mb-8">Fill out the form below and we'll reach out shortly.</p>

                    <form
                      id="access-control-consultation"
                      data-readdy-form
                      onSubmit={handleFormSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-medium text-[#0A1628] mb-2">Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-[#0A1628] mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(416) 555-0123"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1628] mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="john@company.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1628] mb-2">Company / Building Name</label>
                        <input
                          type="text"
                          name="companyName"
                          placeholder="Your Company or Building Name"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-[#0A1628] mb-2">Interested Solution *</label>
                        <input type="hidden" name="solution" value={selectedSolution} />
                        <button
                          type="button"
                          onClick={() => setSolutionDropdownOpen(!solutionDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all cursor-pointer"
                        >
                          <span className={selectedSolution ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedSolution || 'Select a solution type'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${solutionDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {solutionDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto">
                            {solutionOptions.map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => {
                                  setSelectedSolution(option);
                                  setSolutionDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${
                                  selectedSolution === option ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5' : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1628] mb-2">Number of Doors / Entry Points</label>
                        <input
                          type="text"
                          name="doorCount"
                          placeholder="e.g. 5 doors, 2 gates"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#0A1628] mb-2">Project Details & Requirements</label>
                        <textarea
                          name="details"
                          rows={4}
                          maxLength={500}
                          placeholder="Describe your facility, current security setup, number of users, and any specific requirements..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedSolution}
                        className="w-full bg-[#D4A853] text-white py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Assessment
                            <i className="ri-arrow-right-line text-lg"></i>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">Privacy Policy</Link>. We'll never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">Ready to Secure Your Facility?</h2>
            <p className="text-xl text-white/90 mb-6 sm:mb-8">Get a free access control assessment from our security experts</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <button 
                onClick={scrollToContact}
                className="bg-white text-[#D4A853] px-8 sm:px-10 py-4 sm:py-4 rounded-full text-lg sm:text-xl font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                Schedule Free Assessment
              </button>
              <a 
                href="tel:18884589181"
                className="border-2 border-white text-white px-8 sm:px-10 py-4 sm:py-4 rounded-full text-lg sm:text-xl font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <Link to="/" className="flex items-center gap-3"><div className="flex flex-col leading-none"><span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span><span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span></div></Link>
            <p className="text-white/60 text-xs sm:text-sm">© 2024 Security Solutions. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white text-xs sm:text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
